import React, { useEffect, useState } from 'react';
import { Box, FormControl, Input, VStack } from 'native-base';
import { useGeneralStyles } from '~/shared/styles';
import {
    observer,
    useCreateGiftCard,
    useSpringBigStore,
    i18nFn,
    checkGiftCardBalance,
    useUpdateGiftCardBalances
} from '@springbig/shared';
import { useToast } from '~/hooks/toast';
import { Caption, CaptionError } from '~/shared/Typography';
import ButtonSpinner from '~/shared/ButtonSpinner';
import Button from '~/shared/Button';

const AddExisting = ({ t }: { t: i18nFn }) => {

    const gStyle = useGeneralStyles();
    const [pinCode, setPinCode] = useState('');
    const [isValidPinCode, setIsValidPinCode] = useState(true);
    const [formattedCardNumber, setFormattedCardNumber] = useState('');
    const [isValidCardNumber, setIsValidCardNumber] = useState(true);
    const [submitError, setSubmitError] = useState('');
    const [balanceData, setBalanceData] = useState('');

    const pinCodePattern = /^\d{4}$/;
    const cardNumberPattern = /^\d{19}$/;

    const { mutate: updateGiftCardBalances } = useUpdateGiftCardBalances();
    const {
        mutate: requestImortExistingCard,
        error: requestActivateCardError,
        isLoading
    } = useCreateGiftCard();

    const { member, merchant } = useSpringBigStore();
    const { successToast } = useToast();

    useEffect(() => {

        (async () => {

            if (isValidPinCode && isValidCardNumber && formattedCardNumber && pinCode) {
                try {
                    const response = member && await checkGiftCardBalance({
                        member_id: member?.id.toString() || '',
                        card_number: formattedCardNumber.replace(/ /g, ''),
                        card_pin: pinCode
                    });

                    response && setBalanceData(response);
                }
                catch (e) {
                    setBalanceData('');
                }
            }
        })();

    }, [isValidPinCode, isValidCardNumber]);

    useEffect(() => {

        requestActivateCardError && setSubmitError(t('giftCard.importExisting.error'));
    }, [requestActivateCardError]);

    const handlePinCodeChange = (text: string) => {

        setPinCode(text);
        setIsValidPinCode(pinCodePattern.test(text));
    };

    const formatCardNumber = (text: string) => {

        setFormattedCardNumber(text);
    };

    const handleCardNumberChange = (text: string) => {

        formatCardNumber(text);
        setIsValidCardNumber(cardNumberPattern.test(text));
    };

    const isSubmitEnabled: boolean = (pinCodePattern.test(pinCode) && cardNumberPattern.test(formattedCardNumber.replace(/ /g, '')) && member?.id && !isLoading) || false;

    const handleSubmit = () => {

        if (isSubmitEnabled) {

            requestImortExistingCard({
                member_id: member?.id.toString() || '',
                card_pin: pinCode,
                card_number: formattedCardNumber,
                purchase_location: '0',
                auto_reload: false,
                auto_reload_amount: '20.00',
                balance: balanceData
            }, {
                onSuccess: (_) => {

                    updateGiftCardBalances({
                        member_id: member.id.toString(),
                    }, {
                        onSuccess: (_) => {

                            observer.emit('close-actionsheet');
                            successToast(t('giftCard.importExisting.success'));
                        },
                    });
                }
            });
        }
    };

    return (
        <VStack space={4} >
            <Box alignItems={'center'}>
                <Caption fontSize={24}>
                    { t('giftCard.importExisting.title', [merchant?.gift_card_alias]) }
                </Caption>
            </Box>

            <FormControl isRequired isInvalid={!isValidCardNumber}>
                <Caption fontSize={18} fontWeight={'200'} mb={1}>
                    { t('giftCard.importExisting.form.number.title', [merchant?.gift_card_alias]) }
                </Caption>
                <Input
                    placeholder={t('giftCard.importExisting.form.number.title', [merchant?.gift_card_alias]).replace('*', '')}
                    onChangeText={handleCardNumberChange}
                    value={formattedCardNumber}
                    keyboardType="numeric"
                    maxLength={19}
                    fontSize={18}
                    borderWidth={0}
                    borderBottomColor={gStyle.button_background}
                    borderBottomWidth={1}
                    returnKeyType='done'
                />
                { !isValidCardNumber &&
                    <CaptionError>
                        { t('giftCard.importExisting.form.number.error') }
                    </CaptionError>
                }
            </FormControl>
            <FormControl isRequired isInvalid={!isValidPinCode}>
                <Caption fontSize={18} fontWeight={'200'} mb={1}>
                    { t('giftCard.importExisting.form.pin.title') }
                </Caption>
                <Input
                    placeholder={t('giftCard.importExisting.form.pin.title').replace('*', '')}
                    onChangeText={handlePinCodeChange}
                    value={pinCode}
                    keyboardType="numeric"
                    maxLength={4}
                    fontSize={18}
                    borderWidth={0}
                    borderBottomColor={gStyle.button_background}
                    borderBottomWidth={1}
                    returnKeyType='done'
                />
                {!isValidPinCode && (
                    <CaptionError>
                        { t('giftCard.importExisting.form.pin.error') }
                    </CaptionError>
                )}
            </FormControl>
            {/*<VStack>
                <Caption fontSize={18} fontWeight={300}>
                { t('general.location') }
                </Caption>
                <LocationDropdown
                    location={location}
                    setLocation={setLocation}
                />
            </VStack>*/}
            { balanceData &&
                <Caption fontSize={18} fontWeight={'200'}>
                    { t('giftCard.importExisting.currentBalance', [balanceData]) }
                </Caption>
            }
            { submitError &&
                <CaptionError>
                    { submitError }
                </CaptionError>
            }
            <Button
                onPress={handleSubmit}
                backgroundColor={gStyle.button_background}
                alignSelf={'center'}
                isDisabled={!isSubmitEnabled}
                h={12}
            >
                { isLoading ?
                    <ButtonSpinner />
                    :
                    <Caption
                        color={gStyle.button_text}
                        fontSize={20}
                        width='100%'
                    >
                        { t('giftCard.importExisting.submitButton') }
                    </Caption>
                }
            </Button>
        </VStack>
    );
};

export default AddExisting;
